import { useState, useRef, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import { Bell, Check, CheckCheck } from 'lucide-react';
import { useNotifications, useMarkNotificationRead, useMarkAllRead } from '../../hooks/useNotifications';
import { useNotificationSocket } from '../../hooks/useSocket';
import { useQueryClient } from '@tanstack/react-query';
import { notificationKeys } from '../../hooks/useNotifications';

export default function NotificationDropdown() {
  const [open, setOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const { data } = useNotifications({ limit: 20 });
  const markRead = useMarkNotificationRead();
  const markAllRead = useMarkAllRead();

  const unreadCount = data?.unreadCount ?? 0;

  // 실시간 알림 수신 시 캐시 무효화
  const handleNewNotification = useCallback(() => {
    void queryClient.invalidateQueries({ queryKey: notificationKeys.all });
  }, [queryClient]);

  useNotificationSocket(handleNewNotification);

  // Click outside
  useEffect(() => {
    function handleClickOutside(e: MouseEvent) {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleItemClick = (notifId: string, link: string | null, isRead: boolean) => {
    if (!isRead) {
      markRead.mutate(notifId);
    }
    setOpen(false);
    if (!link) return;
    // 레거시 링크(/issues/uuid)는 projectKey가 없어 라우트 매칭이 안 됨 — 흰 화면 방지
    if (/^\/issues\/[^/]+$/.test(link) || /^\/testcases\/[^/]+$/.test(link)) {
      toast.error('오래된 알림이라 이동할 수 없습니다.');
      return;
    }
    navigate(link);
  };

  const handleMarkAllRead = () => {
    markAllRead.mutate();
  };

  const timeAgo = (dateStr: string) => {
    const diff = Date.now() - new Date(dateStr).getTime();
    const mins = Math.floor(diff / 60000);
    if (mins < 1) return '방금';
    if (mins < 60) return `${mins}분 전`;
    const hours = Math.floor(mins / 60);
    if (hours < 24) return `${hours}시간 전`;
    const days = Math.floor(hours / 24);
    return `${days}일 전`;
  };

  return (
    <div ref={dropdownRef} className="relative">
      <button
        onClick={() => setOpen(!open)}
        className="relative p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
      >
        <Bell className="size-5" />
        {unreadCount > 0 && (
          <span className="absolute top-0.5 right-0.5 min-w-[16px] h-4 flex items-center justify-center px-1 text-[11px] font-bold text-white bg-red-500 rounded-full">
            {unreadCount > 99 ? '99+' : unreadCount}
          </span>
        )}
      </button>

      {open && (
        <div className="absolute right-0 mt-1 w-80 bg-white border border-gray-200 rounded-xl shadow-lg z-50 overflow-hidden">
          {/* 헤더 */}
          <div className="flex items-center justify-between px-4 py-2.5 border-b border-gray-100">
            <span className="text-sm font-semibold text-gray-900">알림</span>
            {unreadCount > 0 && (
              <button
                onClick={handleMarkAllRead}
                className="flex items-center gap-1 text-sm text-primary-600 hover:text-primary-700"
              >
                <CheckCheck className="size-3.5" />
                모두 읽음
              </button>
            )}
          </div>

          {/* 알림 목록 */}
          <div className="max-h-80 overflow-y-auto">
            {(!data || data.items.length === 0) ? (
              <div className="py-8 text-center text-sm text-gray-400">알림이 없습니다.</div>
            ) : (
              data.items.map((notif) => (
                <button
                  key={notif.id}
                  onClick={() => handleItemClick(notif.id, notif.link, notif.isRead)}
                  className={`w-full text-left px-4 py-3 border-b border-gray-50 last:border-0 hover:bg-gray-50 transition-colors ${
                    !notif.isRead ? 'bg-blue-50/50' : ''
                  }`}
                >
                  <div className="flex items-start gap-2">
                    {!notif.isRead && (
                      <span className="size-2 bg-primary-500 rounded-full mt-1.5 flex-shrink-0" />
                    )}
                    {notif.isRead && (
                      <Check className="size-3.5 text-gray-300 mt-1 flex-shrink-0" />
                    )}
                    <div className="flex-1 min-w-0">
                      <p className={`text-sm leading-relaxed ${!notif.isRead ? 'text-gray-900' : 'text-gray-600'}`}>
                        {notif.message}
                      </p>
                      <p className="text-[11px] text-gray-400 mt-0.5">{timeAgo(notif.createdAt)}</p>
                    </div>
                  </div>
                </button>
              ))
            )}
          </div>
        </div>
      )}
    </div>
  );
}
